<script setup lang="ts">
import { inject, provide } from "vue";

import { keyboardNavigation } from "@/directives/keyboardNavigation";

import { menuListConfigKey, menuListItemsContextKey } from "./injectKeys";

const props = defineProps<{
  static?: boolean; // static makes the whole list readonly/non-focusable
  multiselect?: boolean;
  label?: string; // accessibility label
  alwaysRendered?: boolean; // Set true for lists that are always rendered in the view (eg. plan readout objectives)
  noScrollOnHover?: boolean; // Prevents scrolling when hovering over list items (default: false)
  role?: string; // ARIA role for the list container (e.g., "menu", "listbox")
}>();

const vKeyboardNavLocal = keyboardNavigation();

// Provide role context for child MenuListItems to determine their own role
provide(menuListItemsContextKey, { role: props.role });

const config = inject(menuListConfigKey, undefined);
</script>

<template>
  <ul v-if="static" tabindex="-1" class="menu-list-items" :role="role || 'listbox'">
    <slot />
  </ul>

  <ul
    v-else
    v-keyboard-nav-local="{
      selector: '.menu-list-item',
      'soft-focus-initial': alwaysRendered,
      'ignore-trigger': !alwaysRendered,
      'no-scroll-on-hover': noScrollOnHover,
      'roving-tabindex': true,
    }"
    class="menu-list-items"
    :role="role || 'listbox'"
    :aria-label="label || $t('list.menuList')"
    :aria-multiselectable="config?.multiselect ? 'true' : undefined"
  >
    <slot />
  </ul>
</template>

<style lang="scss" scoped>
.menu-list-items {
  position: relative;
  flex-grow: 1;
  padding: 2px 4px;
  display: flex;
  flex-direction: column;
  overflow: hidden auto;
  max-height: 100%;

  // when MenuListItems is the first child (no header), add extra top padding
  // to match the header's top padding
  &:first-child {
    padding-top: 4px;
  }
}
</style>
